/**
 * AddLegalEventDialog — shared "Add legal event" modal used by the Legal
 * Calendar and the per-case Legal Schedule tab.
 *
 * Extends the original calendar dialog with:
 *   • Diary hard-limits — dates beyond the TL-configured cap (Portal
 *     Settings → Legal Diary) require a logged justification, e.g.
 *     "awaiting hearing date — chaser email sent to court".
 *   • Optional Court attachment for hearing/petition-type events, via the
 *     GOV.UK court finder. Defaults to the case's logged issuing court.
 */
import { useState, useEffect, useMemo } from "react";
import { Scale, Plus, X, Loader2, Landmark, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { listLegalCases, createLegalEvent } from "@/lib/api";
import CourtPicker, { formatCourtAddress } from "./CourtPicker";
import { COURT_EVENT_TYPES } from "./legalPriority";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

function isoAddDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export default function AddLegalEventDialog({
  open, onClose, eventTypes, defaultDate, onCreated,
  fixedCase = null,          // lock the dialog to one case (Legal Schedule tab)
  diarySettings = null,      // legal-diary-settings doc (caps + override policy)
  caseCourts = null,         // case's issuing_courts {company, guarantor} — offered as defaults
}) {
  const [caseId, setCaseId]           = useState("");
  const [type, setType]               = useState("");
  const [customLabel, setCustomLabel] = useState("");
  const [date, setDate]               = useState(defaultDate);
  const [notes, setNotes]             = useState("");
  const [autoFollowups, setAutoFollowups] = useState(true);
  const [justification, setJustification] = useState("");
  const [busy, setBusy]               = useState(false);

  // Court attachment (court-related event types only). The case may have
  // separate company + guarantor issuing courts (different jurisdictions).
  const savedCourts = [
    caseCourts?.company   && { key: "company",   label: "Company court",   court: caseCourts.company },
    caseCourts?.guarantor && { key: "guarantor", label: "Guarantor court", court: caseCourts.guarantor },
  ].filter(Boolean);
  const [courtChoice, setCourtChoice]         = useState("none");
  const [pickedCourt, setPickedCourt]         = useState(null);

  // Case picker (legal cases only) — skipped entirely when fixedCase given
  const [casesQuery, setCasesQuery] = useState("");
  const [caseOptions, setCaseOptions] = useState([]);
  useEffect(() => {
    if (!open || fixedCase) return;
    const t = setTimeout(async () => {
      try {
        const r = await listLegalCases({ search: casesQuery, limit: 30 });
        setCaseOptions(r.cases || []);
      } catch (_e) { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [open, casesQuery, fixedCase]);

  // Reset form ONLY when the dialog transitions closed → open. Depending on
  // `fixedCase`/`defaultDate` (fresh objects on every parent render) used to
  // wipe the form while the user was typing (Emma, bug 4d25db0384).
  const fixedCaseId = fixedCase?.case_id || "";
  useEffect(() => {
    if (open) {
      setCaseId(fixedCaseId); setType(""); setCustomLabel("");
      setDate(defaultDate); setNotes(""); setAutoFollowups(true);
      setCasesQuery(""); setBusy(false); setJustification("");
      setCourtChoice(savedCourts[0]?.key || "search"); setPickedCourt(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fixedCaseId]);

  const selectedType = eventTypes.find(t => t.key === type);
  const hasFollowups = !!(selectedType?.auto_followups?.length);
  const isCourtType  = COURT_EVENT_TYPES.has(type);

  // ── Diary hard-limit (Portal Settings → Legal Diary) ──────────────────
  const capDays = useMemo(() => {
    if (!diarySettings || !type) return null;
    const per = diarySettings.per_type_max_days || {};
    const v = per[type];
    return v != null ? v : (diarySettings.default_max_days_ahead ?? 30);
  }, [diarySettings, type]);
  const maxIso = capDays != null ? isoAddDays(capDays) : null;
  const exceedsCap = !!(date && maxIso && date > maxIso);
  const overrideAllowed = diarySettings?.allow_override_with_justification !== false;

  const effectiveCourt = !isCourtType
    ? null
    : courtChoice === "search"
      ? pickedCourt
      : savedCourts.find(s => s.key === courtChoice)?.court || null;

  const submit = async () => {
    if (!caseId)         { toast.error("Pick a case first"); return; }
    if (!type)           { toast.error("Pick an event type"); return; }
    if (!date)           { toast.error("Pick a date"); return; }
    if (type === "other" && !customLabel.trim()) { toast.error("Add a custom label"); return; }
    if (exceedsCap && !overrideAllowed) {
      toast.error(`Cannot diarise more than ${capDays} days ahead — overrides are disabled in Portal Settings.`);
      return;
    }
    if (exceedsCap && !justification.trim()) {
      toast.error("A justification is required to schedule beyond the diary limit.");
      return;
    }
    setBusy(true);
    try {
      const r = await createLegalEvent({
        case_id: caseId,
        type,
        date,
        custom_label: type === "other" ? customLabel.trim() : null,
        notes: notes.trim() || null,
        create_auto_followups: autoFollowups,
        court: effectiveCourt ? {
          name: effectiveCourt.name,
          slug: effectiveCourt.slug,
          address: effectiveCourt.address,
          dx_number: effectiveCourt.dx_number,
          distance_miles: effectiveCourt.distance_miles,
          url: effectiveCourt.url,
          source_label: effectiveCourt.source_label,
          source_address: effectiveCourt.source_address,
        } : null,
        override_justification: exceedsCap ? justification.trim() : null,
      });
      toast.success(`Created ${r.created} event${r.created === 1 ? "" : "s"}`);
      onCreated?.();
    } catch (e) {
      toast.error("Failed: " + errorDetailMessage(e, e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="lcal-add-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="w-4 h-4 text-red-700" />
            Add legal event
          </DialogTitle>
          <DialogDescription className="text-xs">
            Diary a legal milestone. Dates beyond the diary limit require a logged justification.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Case picker — or locked case banner */}
          {fixedCase ? (
            <div className="text-xs bg-slate-50 border rounded p-2" data-testid="lcal-add-fixed-case">
              <span className="text-muted-foreground">Case:</span>{" "}
              <span className="font-semibold">{fixedCase.customer_name}</span>
              {fixedCase.loan_application_id && (
                <span className="font-mono text-muted-foreground ml-1">· {fixedCase.loan_application_id}</span>
              )}
            </div>
          ) : (
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Case</label>
              <Input
                placeholder="Search by SF# / customer…"
                value={casesQuery}
                onChange={(e) => setCasesQuery(e.target.value)}
                className="h-9 text-sm mt-0.5"
                data-testid="lcal-add-case-search"
              />
              <Select value={caseId} onValueChange={setCaseId}>
                <SelectTrigger className="h-9 text-sm mt-1" data-testid="lcal-add-case">
                  <SelectValue placeholder={caseOptions.length === 0 ? "No matching cases" : "— Pick a legal case —"} />
                </SelectTrigger>
                <SelectContent>
                  {caseOptions.map(c => (
                    <SelectItem key={c.case_id} value={c.case_id}>
                      {c.customer_name} · {c.loan_application_id || "no SF#"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Type */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Event type</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="h-9 text-sm mt-0.5" data-testid="lcal-add-type">
                <SelectValue placeholder="— Pick an event type —" />
              </SelectTrigger>
              <SelectContent>
                {[...eventTypes].sort((a, b) => (a.label || "").localeCompare(b.label || "", "en", { sensitivity: "base" })).map(t => (
                  <SelectItem key={t.key} value={t.key}>
                    {t.label}{t.auto_followups?.length ? ` (+${t.auto_followups.length} auto)` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {type === "other" && (
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Custom label</label>
              <Input
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                placeholder="e.g. Application to remove charging order"
                className="h-9 text-sm mt-0.5"
                data-testid="lcal-add-custom-label"
              />
            </div>
          )}

          {/* Date + cap hint */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Date</label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-9 text-sm mt-0.5"
              data-testid="lcal-add-date"
            />
            {type && capDays != null && !exceedsCap && (
              <p className="text-[10px] text-muted-foreground mt-0.5">
                Diary limit for this type: {capDays} day{capDays === 1 ? "" : "s"} ahead (until {maxIso}).
              </p>
            )}
          </div>

          {/* Diary hard-limit — justification required beyond the cap */}
          {exceedsCap && (
            overrideAllowed ? (
              <div className="text-xs bg-amber-50 border border-amber-300 rounded p-2 space-y-1.5" data-testid="lcal-add-cap-warning">
                <div className="flex items-start gap-1.5 text-amber-900">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>
                    This is <strong>beyond the {capDays}-day diary limit</strong> for{" "}
                    {selectedType?.label || "this type"} (latest {maxIso}). A justification is required and will be
                    logged on the case — e.g. <em>"awaiting hearing date — chaser email sent to court"</em>.
                  </span>
                </div>
                <textarea
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  rows={2}
                  maxLength={300}
                  className="w-full text-xs border border-amber-300 rounded px-2 py-1.5 resize-none focus:outline-none focus:ring-2 focus:ring-amber-500/30 bg-white"
                  placeholder="Why does this need to be diarised beyond the limit?"
                  data-testid="lcal-add-justification"
                />
              </div>
            ) : (
              <div className="text-xs bg-red-50 border border-red-300 rounded p-2 flex items-start gap-1.5 text-red-900" data-testid="lcal-add-cap-blocked">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  Beyond the {capDays}-day diary limit (latest {maxIso}) — overrides are disabled in Portal Settings.
                  Pick an earlier date or ask a TL to adjust the limits.
                </span>
              </div>
            )
          )}

          {/* Court attachment — hearing / petition events only. Cases can
              have separate company + guarantor issuing courts. */}
          {isCourtType && (
            <div className="border rounded-md p-2 space-y-1.5 bg-slate-50/60" data-testid="lcal-add-court-section">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground flex items-center gap-1">
                <Landmark className="w-3 h-3" /> Court (optional)
              </div>
              {savedCourts.length > 0 && (
                <div className="space-y-1">
                  {savedCourts.map(s => (
                    <label key={s.key} className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
                      <input
                        type="radio"
                        name="court-choice"
                        checked={courtChoice === s.key}
                        onChange={() => setCourtChoice(s.key)}
                        className="mt-0.5"
                        data-testid={`lcal-add-court-${s.key}`}
                      />
                      <span>
                        <strong>{s.label}:</strong> {s.court.name}
                      </span>
                    </label>
                  ))}
                  <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
                    <input
                      type="radio"
                      name="court-choice"
                      checked={courtChoice === "search"}
                      onChange={() => setCourtChoice("search")}
                      className="mt-0.5"
                      data-testid="lcal-add-court-search-radio"
                    />
                    <span>Another court…</span>
                  </label>
                </div>
              )}
              {(savedCourts.length === 0 || courtChoice === "search") && (
                pickedCourt ? (
                  <SelectedCourtRow court={pickedCourt} onClear={() => setPickedCourt(null)} />
                ) : (
                  <CourtPicker compact onSelect={setPickedCourt} />
                )
              )}
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={500}
              className="w-full text-sm border rounded-md px-2 py-1.5 mt-0.5 resize-none focus:outline-none focus:ring-2 focus:ring-red-500/30"
              data-testid="lcal-add-notes"
              placeholder="Anything ops should remember about this event…"
            />
          </div>

          {/* Auto-followups toggle */}
          {hasFollowups && (
            <label className="flex items-start gap-2 text-xs text-slate-700 bg-amber-50 border border-amber-200 rounded p-2">
              <input
                type="checkbox"
                checked={autoFollowups}
                onChange={(e) => setAutoFollowups(e.target.checked)}
                className="mt-0.5"
                data-testid="lcal-add-auto"
              />
              <span>
                Also auto-create {selectedType.auto_followups.length} follow-up event{selectedType.auto_followups.length === 1 ? "" : "s"}:{" "}
                {selectedType.auto_followups.map((f, i) => (
                  <span key={f.type}>
                    <span className="font-medium">{f.type.replace(/_/g, " ")}</span> (+{f.days}d){i < selectedType.auto_followups.length - 1 ? ", " : ""}
                  </span>
                ))}
              </span>
            </label>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || (exceedsCap && !overrideAllowed)}
            className="bg-red-700 hover:bg-red-800"
            data-testid="lcal-add-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            Add event
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SelectedCourtRow({ court, onClear }) {
  return (
    <div className="flex items-start justify-between gap-2 text-xs bg-white border rounded p-2" data-testid="lcal-add-selected-court">
      <div className="min-w-0">
        <div className="font-semibold text-slate-800">{court.name}</div>
        <div className="text-[10px] text-muted-foreground truncate">{formatCourtAddress(court)}</div>
      </div>
      <button type="button" onClick={onClear} className="text-slate-400 hover:text-slate-600 shrink-0" title="Remove court">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
